/**
 * @coeldery/chat-thread — 對話訊息串（含串流中／錯誤狀態）
 *
 * 為何要模組：對話串係 AI 產品最核心嘅表面（第二個 app 用得到）→ 依 R3 做成模組。
 * ⚠️ 同庫內 `chat-message-list`（`common` 但**實驗中**）功能重疊 → `pending_changes.md` **P-44**。
 *
 * 規格：
 *   · 只顯示 `role`（user／assistant）＋ 文字；**唔顯示頭像／真名**（呼應 `rules.md` A4／B3 匿名感）
 *   · 串流中最後一條訊息有 `streaming` 標記（視覺上唔會跳）；自動捲到底
 *   · 錯誤：`state:'error'` → 用文字講清楚＋可選重試鍵（唔准靜靜失敗，`rules.md` B5）
 *   · ⚠️ **`note` 欄位**：安全層 `hold_song` 之類嘅系統提示由呼叫方傳入（本模組唔判斷安全）
 *   · 文字一律 i18n key；顏色只用 CSS var
 */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { splitSentences } from './sentences.ts';
export { splitSentences } from './sentences.ts';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  state?: 'done' | 'streaming' | 'error';
  /** 系統提示（例：作歌暫停）；由呼叫方決定，唔屬訊息內容 */
  noteKey?: string;
  /**
   * ⭐ 1.3.0（`decisions/025`）：訊息種類 ——
   *   `text`（普通）／`understanding`（我聽到嘅係…）／`lyrics`（歌詞版本）／`song`（出咗歌）
   *   ⚠️ 本模組**唔判斷**種類嘅意義，只係用嚟調整闊度同交畀父層掛動作（`renderActions`）
   */
  kind?: 'text' | 'understanding' | 'lyrics' | 'song';
  /** ⭐ 1.3.0：歌詞／歌訊息指向嘅歌曲 id（父層靠佢出〔出歌〕等掣） */
  songId?: string | null;
}

export interface ChatThreadProps {
  messages: ChatMessage[];
  /** 等第一個字嘅提示（例：chat.thinking） */
  pendingKey?: string;
  /** 有冇正在等回應 */
  pending?: boolean;
  /** 錯誤提示 key（配合 `state:'error'` 嗰條訊息） */
  onRetry?: () => void;
  retryLabelKey?: string;
  /**
   * ⭐ 1.1.0（2026-09-21）：畀用戶「**引用**」某條訊息（父層通常帶入輸入框）
   *   · 為何要：手機上「喺輸入框長按揀字」太難發現 → 逐條訊息一個細掣最容易用
   *   · 唔傳 ＝ 唔顯示（向後兼容）
   */
  onQuote?: (text: string) => void;
  quoteLabelKey?: string;
  /**
   * ⭐ 1.3.0（`decisions/025`）：**訊息動作**（由父層砌；例：歌詞訊息嘅〔出歌〕〔複製〕〔叫 AI 改〕、
   *   歌訊息嘅〔▶ 播〕〔分享〕〔換風格〕）—— 顯示喺氣泡**右下角**
   *   · 唔傳 ＝ 唔顯示（向後兼容）
   */
  renderActions?: (m: ChatMessage) => React.ReactNode;
  /**
   * ⭐ 1.4.0：**訊息**揀句（必須有 `selectable`）—— 只對 `role='user'` 嘅訊息生效
   *   · 用戶訊息會逐句變成可撳（唔靠長按拖選：手機系統選取冇可靠 API）
   */
  selectable?: boolean;
  /** 撳咗邊句（父層負責加落 pinned） */
  onPickPhrase?: (text: string) => void;
  /** 已經標記咗嘅句子（用嚟著燈） */
  picked?: string[];
  /**
   * ⭐ 1.4.0：**插位**（顯示喺氣泡內、動作掣**之前**）—— 歌訊息要放播放器
   */
  renderExtra?: (m: ChatMessage) => React.ReactNode;
}

export default function ChatThread({
  messages,
  pending = false,
  pendingKey = 'chat.thinking',
  onRetry,
  retryLabelKey = 'chat.retry',
  onQuote,
  quoteLabelKey = 'chat_quote',
  renderActions,
  selectable = false,
  onPickPhrase,
  picked,
  renderExtra,
}: ChatThreadProps) {
  const { t } = useTranslation();
  const bottomRef = useRef<HTMLDivElement>(null);

  /** 自動捲到底（新訊息／串流中都要跟住） */
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length, messages[messages.length - 1]?.text, pending]);

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '16px 12px' }}
      aria-live="polite"
      aria-busy={pending}
    >
      {messages.map((m) => {
        const mine = m.role === 'user';
        /** ⭐ 1.4.0：插位內容（唔係每條訊息都有 → **唔准**空 div 加邊距，否則每條訊息都多 10px 白位） */
        const extra = renderExtra ? renderExtra(m) : null;
        return (
          <div key={m.id} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start' }}>
            <div
              style={{
                /* ⭐ 1.4.1：`song`／`lyrics` 要 `width: 100%` —— 以前只有 `maxWidth` → flex **shrink-to-fit**
                   → 歌訊息（文字短）縮窄 → 內嵌播放器好窄（血證：產品負責人 2026-09-22） */
                width: m.kind === 'lyrics' || m.kind === 'song' ? '100%' : undefined,
                minWidth: m.kind === 'lyrics' || m.kind === 'song' ? 0 : undefined,
                maxWidth: m.kind === 'lyrics' || m.kind === 'song' ? '100%' : '82%',
                padding: '10px 14px',
                borderRadius: '16px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                lineHeight: 1.6,
                fontSize: 'var(--font-size-body, 18px)',
                backgroundColor: mine ? 'var(--color-primary)' : 'var(--color-card)',
                color: mine ? 'var(--color-on-primary)' : 'var(--color-text)',
                border: mine ? 'none' : '1px solid var(--color-divider)',
                opacity: m.state === 'streaming' ? 0.96 : 1,
              }}
            >
              {/* ⭐ 1.4.0：標記模式 → 用戶訊息逐句可撳（再撳取消）；其餘照舊顯示原文 */}
              {selectable && mine ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  {splitSentences(m.text).map((sentence, si) => {
                    const on = Boolean(picked?.includes(sentence));
                    return (
                      <button
                        key={`${si}-${sentence}`}
                        type="button"
                        data-testid="pick-sentence"
                        aria-pressed={on}
                        onClick={() => onPickPhrase?.(sentence)}
                        style={{
                          textAlign: 'left',
                          padding: '1px 0',
                          border: 'none',
                          borderBottom: on ? '1.5px solid currentColor' : '1px dashed currentColor',
                          background: on ? 'rgba(255, 255, 255, 0.28)' : 'transparent',
                          color: 'inherit',
                          font: 'inherit',
                          cursor: 'pointer',
                          opacity: on ? 1 : 0.92,
                        }}
                      >
                        {sentence}
                      </button>
                    );
                  })}
                </div>
              ) : (
                m.text
              )}
              {m.state === 'streaming' ? (
                <span aria-hidden="true" style={{ opacity: 0.5 }}>
                  {' '}
                  ▍
                </span>
              ) : null}
              {/* ⭐ 1.4.0：插位（歌訊息嘅播放器／歌詞訊息嘅編輯器）—— 喺動作掣之前；冇內容就唔渲染 */}
              {extra ? <div style={{ marginTop: '10px' }}>{extra}</div> : null}
              {/* ⭐ 1.3.0：訊息動作（由父層砌）—— 歌詞／歌嘅〔出歌〕〔播〕〔分享〕等 */}
              {renderActions ? (
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '6px',
                    marginTop: '10px',
                    justifyContent: 'flex-end',
                  }}
                >
                  {renderActions(m)}
                </div>
              ) : null}
              {m.noteKey ? (
                <div
                  style={{
                    marginTop: '8px',
                    paddingTop: '8px',
                    borderTop: '1px solid var(--color-divider)',
                    fontSize: 'var(--font-size-small, 14px)',
                    color: 'var(--color-text-secondary)',
                  }}
                >
                  {t(m.noteKey)}
                </div>
              ) : null}
              {onQuote ? (
                <button
                  type="button"
                  onClick={() => onQuote(m.text)}
                  aria-label={`${t(quoteLabelKey)}：${m.text.slice(0, 20)}`}
                  style={{
                    display: 'block',
                    marginTop: '6px',
                    minHeight: '32px',
                    padding: '2px 12px',
                    border: '1px solid var(--color-divider)',
                    borderRadius: '999px',
                    background: 'transparent',
                    color: mine ? 'var(--color-on-primary)' : 'var(--color-text-secondary)',
                    fontFamily: 'inherit',
                    fontSize: 'var(--font-size-tiny, 13.5px)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    opacity: 0.9,
                  }}
                >
                  {t(quoteLabelKey)}
                </button>
              ) : null}
              {m.state === 'error' && onRetry ? (
                <button
                  type="button"
                  onClick={onRetry}
                  style={{
                    display: 'block',
                    marginTop: '10px',
                    minHeight: '44px',
                    padding: '0 16px',
                    border: '1.5px solid var(--color-accent)',
                    borderRadius: '22px',
                    background: 'none',
                    color: 'var(--color-accent)',
                    fontFamily: 'inherit',
                    fontSize: 'var(--font-size-button, 17px)',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  {t(retryLabelKey)}
                </button>
              ) : null}
            </div>
          </div>
        );
      })}
      {pending ? (
        <p style={{ margin: 0, fontSize: 'var(--font-size-small, 14px)', color: 'var(--color-text-secondary)' }}>{t(pendingKey)}</p>
      ) : null}
      <div ref={bottomRef} />
    </div>
  );
}
